// CreateCourse.tsx
import useReactHookForm from "hooks/useReactHookForm";
import { useAppDispatch } from "hooks/redux";
import { Button, Grid, Paper } from "@mui/material";
import DynamicForm from "shared/organisms/dynamicform";
import ContentRight from "shared/molecules/content-right";
import { TypographyHeading } from "shared/molecules/typography-layout";
import { useMutation } from "@apollo/client";
import entitesClient from "utils/graphql/entitiesClient";
import LoadingButton from "@mui/lab/LoadingButton";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { useHistory } from "react-router-dom";
import { isFieldsValidate } from "shared/organisms/dynamicform/helper";
import { CREATE_COURSE } from "utils/graphql/services/global-content-hierarchy/mutation";
import { courseInitialValues, createCourseProperties, courseValidationSchema , courseMandatoryFields} from "./helper";

export const CreateCourse = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  // react-hook-form
  const { control, errors, getValues, watch, setValue } = useReactHookForm(
    courseInitialValues,
    courseValidationSchema,
    "" // This is likely the mode, e.g., 'onChange', 'onBlur', etc.
  );

  // WATCH THE MANDATORY FIELDS TO ENSURE THE COMPONENT RE-RENDERS
  // AND THE 'DISABLED' PROP RE-EVALUATES WHENEVER THESE VALUES CHANGE.
  const watchedMandatoryFields = watch(courseMandatoryFields.map(field => field.id));

  // GraphQL mutation
  const [createCourse, { loading }] = useMutation(CREATE_COURSE, {
    client: entitesClient,
  });

  // Handle create course
  const handleCreateCourse = async () => {
    try {
      // NOTE: Using getValues() is safer here than watch() for the submit handler,
      // as it avoids unnecessary re-renders during component lifecycle.
      const values = getValues(); 
      const response: any = await createCourse({
        variables: {
          input: {
            course_name: values?.course_name,
            gcf_public_course_id: values?.gcf_public_course_id,
            // pass payload 
            npractice_practice_program_id: values?.npractice_practice_program_id,
            display_name: values?.display_name,
            image_url: values?.image_url,
          },
        },
      });

      if (response?.data?.createCourse?.success) {
        dispatch(
          snackbarRequest({
            message: response.data.createCourse.message || "Course created successfully",
          })
        );
        history.replace("/global-content-hierarchy/courses"); // navigate back to courses list
      } else {
        dispatch(
          snackbarRequest({
            message: response?.data?.createCourse?.message || "Failed to create course",
            type: "error",
          })
        );
      }

    } catch (err: any) {
      dispatch(
        snackbarRequest({
          message: err?.message || "Something went wrong",
          type: "error",
        })
      );
    }
  };

  // Check validation status based on errors and current values
  const isFormValid = isFieldsValidate(errors, courseMandatoryFields, getValues);

  return (
    <div>
      <TypographyHeading>Create Course</TypographyHeading>

      <Paper elevation={2} sx={{ px: 2, pb: 2, pt: 2 }}>
        <Grid container spacing={2}>
          <DynamicForm
            fields={createCourseProperties} 
            control={control}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
          />

          <ContentRight sx={{ mt: 1 }}>
            <LoadingButton
              variant="contained"
              // REMOVED loadingPosition="start" to fix the MUI warning
              sx={{
                width: "200px",
                height: "40px",
                marginTop: "15px",
                marginRight: "10px",
                padding: "7px 23.5px 7px 23.5px",
                cursor: "pointer",
                textAlign: "center",
              }}
              onClick={handleCreateCourse}
              // USE the pre-calculated validity state
              disabled={!isFormValid}
              loading={loading}
            >
              Create Course
            </LoadingButton>
          </ContentRight>
        </Grid>
      </Paper>
    </div>
  );
};